/**
 * API Service
 *
 * Centralized service for all HTTP requests to the Bingo API.
 * Handles authentication, error handling, request/response interceptors,
 * and provides typed methods for all API endpoints.
 *
 * @module services/api
 */

import axios from 'axios';
import { API_CONFIG, API_ENDPOINTS, STORAGE_KEYS, ERROR_MESSAGES } from '../utils/constants';

/**
 * Create axios instance with default configuration
 */
const apiClient = axios.create({
  baseURL: API_CONFIG.BASE_URL,
  timeout: API_CONFIG.TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
  },
});

/**
 * Request interceptor - Add auth token to requests
 */
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

/**
 * Response interceptor - Handle errors globally
 */
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.code === 'ECONNABORTED') {
      error.message = ERROR_MESSAGES.TIMEOUT;
    } else if (!error.response) {
      error.message = ERROR_MESSAGES.NETWORK_ERROR;
    } else {
      switch (error.response.status) {
        case 401:
          error.message = ERROR_MESSAGES.UNAUTHORIZED;
          // Clear auth data and redirect to login
          localStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN);
          localStorage.removeItem(STORAGE_KEYS.USER_DATA);
          window.location.href = '/login';
          break;
        case 403:
          error.message = ERROR_MESSAGES.FORBIDDEN;
          break;
        case 404:
          error.message = ERROR_MESSAGES.NOT_FOUND;
          break;
        case 422:
          error.message = ERROR_MESSAGES.VALIDATION_ERROR;
          break;
        case 500:
        case 502:
        case 503:
        case 504:
          error.message = ERROR_MESSAGES.SERVER_ERROR;
          break;
        default:
          error.message = error.response.data?.message || error.message;
      }
    }
    return Promise.reject(error);
  }
);

/**
 * API Service Object
 */
const api = {
  // ==================== Authentication ====================

  /**
   * Login user
   * @param {Object} credentials - User credentials
   * @param {string} credentials.email - User email
   * @param {string} credentials.password - User password
   * @returns {Promise<Object>} User data and token
   */
  login: async (credentials) => {
    const response = await apiClient.post(API_ENDPOINTS.LOGIN, credentials);
    if (response.data.token) {
      localStorage.setItem(STORAGE_KEYS.AUTH_TOKEN, response.data.token);
      localStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(response.data.user));
    }
    return response.data;
  },

  /**
   * Register new user
   * @param {Object} userData - User registration data
   * @returns {Promise<Object>} Created user data
   */
  register: async (userData) => {
    const response = await apiClient.post(API_ENDPOINTS.REGISTER, userData);
    return response.data;
  },

  /**
   * Logout user
   */
  logout: () => {
    localStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.USER_DATA);
    window.location.href = '/login';
  },

  /**
   * Get current user profile
   * @returns {Promise<Object>} User profile data
   */
  getProfile: async () => {
    const response = await apiClient.get(API_ENDPOINTS.PROFILE);
    return response.data;
  },

  // ==================== Players ====================

  /**
   * Get all players
   * @param {Object} params - Query parameters
   * @returns {Promise<Array>} List of players
   */
  getPlayers: async (params = {}) => {
    const response = await apiClient.get(API_ENDPOINTS.PLAYERS, { params });
    return response.data;
  },

  /**
   * Get player by ID
   * @param {string} id - Player ID
   * @returns {Promise<Object>} Player data
   */
  getPlayerById: async (id) => {
    const response = await apiClient.get(API_ENDPOINTS.PLAYER_BY_ID(id));
    return response.data;
  },

  /**
   * Update player
   * @param {string} id - Player ID
   * @param {Object} data - Updated player data
   * @returns {Promise<Object>} Updated player
   */
  updatePlayer: async (id, data) => {
    const response = await apiClient.put(API_ENDPOINTS.PLAYER_BY_ID(id), data);
    return response.data;
  },

  /**
   * Update player balance
   * @param {string} id - Player ID
   * @param {Object} data - Balance update data
   * @param {number} data.amount - Amount to add/subtract
   * @param {string} data.type - Transaction type
   * @returns {Promise<Object>} Updated balance
   */
  updatePlayerBalance: async (id, data) => {
    const response = await apiClient.post(API_ENDPOINTS.PLAYER_BALANCE(id), data);
    return response.data;
  },

  /**
   * Delete player
   * @param {string} id - Player ID
   * @returns {Promise<void>}
   */
  deletePlayer: async (id) => {
    const response = await apiClient.delete(API_ENDPOINTS.PLAYER_BY_ID(id));
    return response.data;
  },

  // ==================== Rooms ====================

  /**
   * Get all rooms
   * @param {Object} params - Query parameters
   * @returns {Promise<Array>} List of rooms
   */
  getRooms: async (params = {}) => {
    const response = await apiClient.get(API_ENDPOINTS.ROOMS, { params });
    return response.data;
  },

  /**
   * Get room by ID
   * @param {string} id - Room ID
   * @returns {Promise<Object>} Room data
   */
  getRoomById: async (id) => {
    const response = await apiClient.get(API_ENDPOINTS.ROOM_BY_ID(id));
    return response.data;
  },

  /**
   * Create new room
   * @param {Object} roomData - Room data
   * @returns {Promise<Object>} Created room
   */
  createRoom: async (roomData) => {
    const response = await apiClient.post(API_ENDPOINTS.ROOMS, roomData);
    return response.data;
  },

  /**
   * Update room
   * @param {string} id - Room ID
   * @param {Object} data - Updated room data
   * @returns {Promise<Object>} Updated room
   */
  updateRoom: async (id, data) => {
    const response = await apiClient.put(API_ENDPOINTS.ROOM_BY_ID(id), data);
    return response.data;
  },

  /**
   * Delete room
   * @param {string} id - Room ID
   * @returns {Promise<void>}
   */
  deleteRoom: async (id) => {
    const response = await apiClient.delete(API_ENDPOINTS.ROOM_BY_ID(id));
    return response.data;
  },

  /**
   * Get players in room
   * @param {string} id - Room ID
   * @returns {Promise<Array>} List of players in room
   */
  getRoomPlayers: async (id) => {
    const response = await apiClient.get(API_ENDPOINTS.ROOM_PLAYERS(id));
    return response.data;
  },

  // ==================== Games ====================

  /**
   * Get all games
   * @param {Object} params - Query parameters
   * @returns {Promise<Array>} List of games
   */
  getGames: async (params = {}) => {
    const response = await apiClient.get(API_ENDPOINTS.GAMES, { params });
    return response.data;
  },

  /**
   * Get game by ID
   * @param {string} id - Game ID
   * @returns {Promise<Object>} Game data
   */
  getGameById: async (id) => {
    const response = await apiClient.get(API_ENDPOINTS.GAME_BY_ID(id));
    return response.data;
  },

  /**
   * Start game
   * @param {string} id - Game ID
   * @returns {Promise<Object>} Started game data
   */
  startGame: async (id) => {
    const response = await apiClient.post(API_ENDPOINTS.GAME_START(id));
    return response.data;
  },

  /**
   * Call number in game
   * @param {string} id - Game ID
   * @returns {Promise<Object>} Called number data
   */
  callNumber: async (id) => {
    const response = await apiClient.post(API_ENDPOINTS.GAME_CALL_NUMBER(id));
    return response.data;
  },

  /**
   * End game
   * @param {string} id - Game ID
   * @returns {Promise<Object>} Ended game data
   */
  endGame: async (id) => {
    const response = await apiClient.post(API_ENDPOINTS.GAME_END(id));
    return response.data;
  },

  // ==================== Bingo Cards ====================

  /**
   * Get all bingo cards
   * @param {Object} params - Query parameters
   * @returns {Promise<Array>} List of bingo cards
   */
  getCards: async (params = {}) => {
    const response = await apiClient.get(API_ENDPOINTS.CARDS, { params });
    return response.data;
  },

  /**
   * Generate bingo cards
   * @param {Object} data - Generation parameters
   * @param {string} data.gameId - Game ID
   * @param {string} data.playerId - Player ID
   * @param {number} data.quantity - Number of cards to generate
   * @returns {Promise<Array>} Generated cards
   */
  generateCards: async (data) => {
    const response = await apiClient.post(API_ENDPOINTS.CARDS_GENERATE, data);
    return response.data;
  },

  /**
   * Get card by ID
   * @param {string} id - Card ID
   * @returns {Promise<Object>} Card data
   */
  getCardById: async (id) => {
    const response = await apiClient.get(API_ENDPOINTS.CARD_BY_ID(id));
    return response.data;
  },

  /**
   * Mark number on card
   * @param {string} id - Card ID
   * @param {Object} data - Mark data
   * @param {number} data.number - Number to mark
   * @returns {Promise<Object>} Updated card
   */
  markCard: async (id, data) => {
    const response = await apiClient.post(API_ENDPOINTS.CARD_MARK(id), data);
    return response.data;
  },

  /**
   * Verify card for winning
   * @param {string} id - Card ID
   * @returns {Promise<Object>} Verification result
   */
  verifyCard: async (id) => {
    const response = await apiClient.post(API_ENDPOINTS.CARD_VERIFY(id));
    return response.data;
  },

  // ==================== Statistics ====================

  /**
   * Get dashboard statistics
   * @returns {Promise<Object>} Dashboard stats
   */
  getStats: async () => {
    // This endpoint might need to be created in your API
    const response = await apiClient.get('/stats/dashboard');
    return response.data;
  },

  /**
   * Get revenue statistics
   * @param {Object} params - Query parameters
   * @returns {Promise<Object>} Revenue stats
   */
  getRevenueStats: async (params = {}) => {
    const response = await apiClient.get('/stats/revenue', { params });
    return response.data;
  },

  /**
   * Get player statistics
   * @param {Object} params - Query parameters
   * @returns {Promise<Object>} Player stats
   */
  getPlayerStats: async (params = {}) => {
    const response = await apiClient.get('/stats/players', { params });
    return response.data;
  },
};

export default api;
export { apiClient };
